<template>
  <q-page padding>
    <div class="q-mb-xl">
      <q-btn
        class="gt-sm q-ml-md"
        color="secondary"
        label="Voltar"
        icon="arrow_back"
        dense
        unelevated
        to="/imoveis"
      />
    </div>  
    <q-card class="q-pa-md">
      <q-card-section>
        <div class="text-h5 text-weight-bold q-mb-md">
          <q-chip color="deep-orange" text-color="white" icon="home">
            Imovél
          </q-chip>
        </div>
      </q-card-section>

      <q-card-section>
        <q-form @submit="onSubmit" @reset="onReset" class="q-gutter-md">
          <!-- Título -->
          <q-input
            v-model="form.title"
            label="Título *"
            hint="Digite um título atrativo para o imóvel"
            lazy-rules
            :rules="[val => val && val.length > 0 || 'Título é obrigatório']"
            outlined
          />

          <!-- Descrição -->
          <q-input
            v-model="form.description"
            label="Descrição *"
            type="textarea"
            rows="5"
            hint="Descreva detalhes importantes do imóvel"
            lazy-rules
            :rules="[val => val && val.length > 0 || 'Descrição é obrigatória']"
            outlined
          />

          <!-- Tipo de Imóvel -->
          <q-select
            v-model="form.property_type_id"
            :options="propertyTypes"
            label="Tipo de Imóvel *"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            lazy-rules
            :rules="[val => val !== null || 'Selecione o tipo de imóvel']"
            outlined
          />

          <!-- Localização -->
          <q-input
            v-model="form.location"
            label="Localização *"
            hint="Endereço do imóvel"
            lazy-rules
            :rules="[val => val && val.length > 0 || 'Localização é obrigatória']"
            outlined
          />

          <!-- Preço e Moeda -->
          <div class="row q-col-gutter-sm">
            <div class="col-8">
              <q-input
                v-model.number="form.price"
                label="Preço *"
                type="number"
                min="0"
                step="0.01"
                prefix="$"
                lazy-rules
                :rules="[val => val >= 0 || 'Preço deve ser maior ou igual a 0']"
                outlined
              />
            </div>
            <div class="col-4">
              <q-select
                v-model="form.currency"
                :options="currencies"
                label="Moeda *"
                lazy-rules
                :rules="[val => val && val.length > 0 || 'Selecione a moeda']"
                outlined
              />
            </div>
          </div>

          <!-- Área -->
          <q-input
            v-model.number="form.area"
            label="Área (m²) *"
            type="number"
            min="0"
            step="0.01"
            suffix="m²"
            lazy-rules
            :rules="[val => val >= 0 || 'Área deve ser maior ou igual a 0']"
            outlined
          />

          <!-- Quartos, Banheiros e Garagem -->
          <div class="row q-col-gutter-sm">
            <div class="col-4">
              <q-input
                v-model.number="form.bedroom_amount"
                label="Quartos *"
                type="number"
                min="0"
                lazy-rules
                :rules="[val => val >= 0 || 'Quantidade inválida']"
                outlined
              />
            </div>
            <div class="col-4">
              <q-input
                v-model.number="form.bathroom_amount"
                label="Banheiros *"
                type="number"
                min="0"
                lazy-rules
                :rules="[val => val >= 0 || 'Quantidade inválida']"
                outlined
              />
            </div>
            <div class="col-4">
              <q-input
                v-model.number="form.garage_space"
                label="Vagas Garagem *"
                type="number"
                min="0"
                lazy-rules
                :rules="[val => val >= 0 || 'Quantidade inválida']"
                outlined
              />
            </div>
          </div>

          <!-- Condição -->
          <q-select
            v-model="form.condition_id"
            :options="conditions"
            label="Condição do Imóvel *"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            lazy-rules
            :rules="[val => val !== null || 'Selecione a condição']"
            outlined
          />

          <!-- Objetivo -->
          <q-select
            v-model="form.objective_id"
            :options="objectives"
            label="Objetivo *"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            lazy-rules
            :rules="[val => val !== null || 'Selecione o objetivo']"
            outlined
          />

          <!-- Upload de Imagens -->
          <div>
            <div class="text-subtitle2 q-mb-sm">
              Imagens do Imóvel * 
              <span class="text-caption text-grey-7">({{ form.images.length }}/10)</span>
            </div>
            
            <q-file
              v-model="tempImage"
              label="Adicionar imagem"
              accept="image/jpeg,image/png,image/jpg,image/webp"
              max-file-size="5242880"
              outlined
              @update:model-value="onImageSelected"
              @rejected="onRejected"
              :disable="form.images.length >= 10"
            >
              <template v-slot:prepend>
                <q-icon name="add_photo_alternate" />
              </template>
              <template v-slot:hint>
                Formatos: JPEG, PNG, JPG, WEBP. Máximo 5MB por imagem. 
                {{ form.images.length >= 10 ? 'Limite atingido' : `Adicione mais ${10 - form.images.length} imagem(ns)` }}
              </template>
            </q-file>

            <!-- Mensagem de erro se não houver imagens -->
            <div v-if="showImageError && form.images.length === 0" class="text-negative text-caption q-mt-xs">
              Adicione pelo menos 1 imagem
            </div>
            <div v-else class="text-caption text-grey-7 q-mt-xs">
              Adicione pelo menos 1 imagem, carregue uma imagem por vez.
            </div>

            <!-- Preview das Imagens -->
            <div v-if="form.images.length > 0" class="q-mt-md">
              <div class="row q-col-gutter-sm">
                <div
                  v-for="(item, index) in imagesPreviews"
                  :key="index"
                  class="col-4 col-sm-3 col-md-2"
                >
                  <q-card>
                    <q-img
                      :src="item.url"
                      ratio="1"
                      style="height: 120px"
                    >
                      <div class="absolute-top-right">
                        <q-btn
                          round
                          dense
                          size="sm"
                          color="negative"
                          icon="close"
                          @click="removeImage(index)"
                        />
                      </div>
                      <div class="absolute-bottom text-center text-caption bg-black-4">
                        {{ item.name }}
                      </div>
                    </q-img>
                  </q-card>
                </div>
              </div>
            </div>
          </div>

          <!-- Botões -->
          <div class="q-mt-lg">
            <q-btn
              label="Publicar Imóvel"
              type="submit"
              color="primary"
              size="md"
              :loading="loading"
              class="q-mr-sm"
            />
            <q-btn
              label="Limpar"
              type="reset"
              color="grey"
              flat
              size="md"
            />
            <q-btn
              label="Cancelar"
              color="negative"
              flat
              size="md"
              to="/imoveis"
            />
          </div>
        </q-form>
      </q-card-section>
      <q-inner-loading
        :showing="loader"
        label="Por favor, aguarde..."
        label-class="text-teal"
        label-style="font-size: 1.1em"
      />
    </q-card>
  </q-page>
</template>

<script>
// eslint-disable no-unused-vars
import store from '@/vuex/store';
import { ref, onBeforeUnmount } from 'vue'
import Swal from 'sweetalert2';
import router from '@/router';

export default {
  name: 'PropertyForm',
  
  setup() {
    const loading = ref(false)
    const loader = ref(false)
    const tempImage = ref(null)
    const showImageError = ref(false)

    const form = ref({
      title: '',
      description: '',
      property_type_id: null,
      location: '',
      price: 0,
      currency: 'MZN',
      area: 0,
      bedroom_amount: 0,
      bathroom_amount: 0,
      garage_space: 0,
      condition_id: null,
      objective_id: null,
      images: []
    })

    const propertyTypes = ref([])
    const conditions = ref([])
    const objectives = ref([])
    const currencies = ref(['MZN', 'USD'])

    // Array para armazenar previews das imagens
    const imagesPreviews = ref([])

    // Limpar URLs ao desmontar componente
    onBeforeUnmount(() => {
      imagesPreviews.value.forEach(preview => {
        URL.revokeObjectURL(preview.url)
      })
    })

    getStoreOptions()
    
    function getStoreOptions() {
      loader.value = true
      store.dispatch('imovel/getImovelOptions').then(() => {
        // console.log('Opções de submissão carregadas:', store.state.imovel.submitOptions)
        propertyTypes.value = store.state.imovel.submitOptions.property_types
        conditions.value = store.state.imovel.submitOptions.conditions
        objectives.value = store.state.imovel.submitOptions.objectives
        loader.value = false
      })
    }

    const onImageSelected = (file) => {
      if (file) {
        if (form.value.images.length < 10) {
          // console.log('=== OBJETO RECEBIDO ===')
          // console.log('Nome original:', file.name)
          // console.log('__key:', file.__key)
          
          // Extrair o nome correto do __key
          // O formato é: timestamp + nome + size
          // Exemplo: "1761134938050photo-1600566753190-17f0baa2a6c3.jpg166640"
          let correctName = file.name
          
          if (file.__key) {
            // Remover o timestamp do início e o size do final
            const keyString = file.__key.toString()
            // Extrair apenas o nome do arquivo (com extensão)
            const match = keyString.match(/\d+(.+\.(jpg|jpeg|png|gif|webp))\d+$/i)
            if (match) {
              correctName = match[1]
            }
          }
          
          console.log('Nome corrigido:', correctName)
          
          // Criar um novo File com o nome correto
          const correctedFile = new File([file], correctName, {
            type: file.type,
            lastModified: file.lastModified
          })
          
          // console.log('File corrigido:', {
          //   name: correctedFile.name,
          //   size: correctedFile.size,
          //   type: correctedFile.type
          // })
          
          // Adiciona o arquivo corrigido
          form.value.images.push(correctedFile)
          
          // Cria preview
          const preview = {
            url: URL.createObjectURL(correctedFile),
            name: `Imagem ${form.value.images.length}`,
            file: correctedFile
          }
          imagesPreviews.value.push(preview)
          
          showImageError.value = false
          console.log(`Imagem ${form.value.images.length} adicionada:`, correctedFile.name)
        } else {
          console.log('Máximo de 10 imagens atingido')
        }
        tempImage.value = null
      }
    }



    const removeImage = (index) => {
      const removedImage = form.value.images[index]
      const removedPreview = imagesPreviews.value[index]
      
      console.log(`Removendo imagem ${index + 1}: ${removedImage.name}`)
      
      // Revoga URL para liberar memória
      URL.revokeObjectURL(removedPreview.url)
      
      // Remove do array
      form.value.images.splice(index, 1)
      imagesPreviews.value.splice(index, 1)
      
      // Atualiza os nomes das imagens restantes
      imagesPreviews.value.forEach((preview, idx) => {
        preview.name = `Imagem ${idx + 1}`
      })
    }

    const onRejected = (rejectedEntries) => {
      rejectedEntries.forEach(entry => {
        if (entry.failedPropValidation === 'max-file-size') {
          console.log(`${entry.file.name} excede 5MB`)
        } else if (entry.failedPropValidation === 'accept') {
          console.log(`${entry.file.name} tem formato inválido`)
        } else if (entry.failedPropValidation === 'max-files') {
          console.log('Máximo de 10 imagens permitido')
        }
      })
    }

    const onSubmit = async () => {
      // Valida imagens antes de enviar
      if (form.value.images.length === 0) {
        showImageError.value = true
        console.log('Adicione pelo menos 1 imagem do imóvel')
        return
      }

      loading.value = true
      loader.value = true

      try {
        const formData = new FormData()
        Object.keys(form.value).forEach(key => {
          if (key !== 'images') {
            formData.append(key, form.value[key])
          }
        })

        // eslint-disable-next-line no-unused-vars
        form.value.images.forEach((image, index) => {
          formData.append('images[]', image)
        })

        await store.dispatch('imovel/storeImovel', formData)
        await Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Imovél publicado com sucesso!",
          showConfirmButton: false,
          timer: 1500
        });
        onReset()
        router.push('/imoveis')
      } catch (error) {
        // console.log('Erro ao publicar imóvel. Tente novamente.')
        
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: error.response.data.error || 'Erro ao publicar imóvel. Tente novamente.',
        });
        console.error(error)
      } finally {
        loading.value = false
        loader.value = false
      }
    }

    const onReset = () => {
      // Limpa URLs dos previews
      imagesPreviews.value.forEach(preview => {
        URL.revokeObjectURL(preview.url)
      })
      
      form.value = {
        title: '',
        description: '',
        property_type_id: null,
        location: '',
        price: 0,
        currency: 'MZN',
        area: 0,
        bedroom_amount: 0,
        bathroom_amount: 0,
        garage_space: 0,
        condition_id: null,
        objective_id: null,
        images: []
      }
      imagesPreviews.value = []
      tempImage.value = null
      showImageError.value = false
    }

    return {
      form,
      loading,
      loader,
      tempImage,
      showImageError,
      propertyTypes,
      conditions,
      objectives,
      currencies,
      imagesPreviews,
      onImageSelected,
      removeImage,
      onRejected,
      onSubmit,
      onReset
    }
  }
}
</script>

<style scoped>
.q-card {
  max-width: 800px;
  margin: 0 auto;
}
</style>